import {
  findCountryRegionTagByName,
  getRegionTagLabel,
  getRegionTagType,
  getRegionTagValueForCountryCode,
  hasRegionTagLabel,
  type RegionTagType,
} from '../facets/region-tag-labels';
import { getFinlandRegionChildren } from '../facets/finland-region-hierarchy';
import { FINLAND_COUNTRY_PLACE_ID, isFinlandRegionTagValue } from '../geocoding/finland-place-ids';

/**
 * TEC-482: turns a job's structured location data (gazetteer `locationIds` + the separate
 * `remoteEligibility` scope) into what the SRP hit cards and the job detail page display when the
 * `dev_enable_new_location_system` flag is on. Pure, no React, shared by both surfaces so the
 * card's "Helsinki +2" pill and the job page's location line can't drift apart.
 *
 * Why a separate path from the legacy `location` strings: those are LLM-extracted freeform text
 * ("Helsinki, Finland", "Remote - EMEA") with the remote scope smuggled in as a pseudo-location.
 * The structured fields resolve each entry to one gazetteer place with per-locale labels
 * (Helsinki / Helsingfors), and carry the remote scope as its own field, so the card can show
 * "Etätyö (EMEA)" next to the work model instead of listing "Remote - EMEA" as a place.
 *
 * Fallback contract: both resolvers return `null` when the job carries no usable structured
 * data (jobs enriched before TEC-482 shipped, until `backfill:job-locations` runs; or ids the
 * client-side label table doesn't know). Callers must then render the legacy `location` strings
 * exactly as before, never a raw numeric placeId.
 */

export type StructuredLocationLocale = 'en' | 'fi' | 'sv';

export interface StructuredJobLocationEntry {
  /** Gazetteer placeId, also this entry's `regionTags` facet value. Empty string only for a
   * global remote scope standing in as the location (nothing to link to; render as plain text). */
  placeId: string;
  label: string;
  type: RegionTagType | 'global';
}

export interface StructuredJobLocationDisplay {
  /** Labeled, prioritized (see resolveStructuredJobLocations), deduped. Never empty. */
  entries: StructuredJobLocationEntry[];
  primary: StructuredJobLocationEntry;
  additionalCount: number;
  /** Compact card form, e.g. "Helsinki +2". */
  display: string;
  /** Every shown entry, comma-joined, e.g. "Helsinki, Espoo, Tampere" (title / aria text). */
  fullList: string;
  /**
   * True when the job has no physical place and the single entry is its remote scope ("Eurooppa",
   * "Globaali"), the structured equivalent of the legacy "Remote - Europe" pseudo-location. Callers
   * then show the plain work model beside it (formatRemotenessWithScope(remoteness, null)) so the
   * scope isn't printed twice on the same card.
   */
  remoteScopeAsLocation: boolean;
}

/**
 * Minimal shape both data sources satisfy: a Typesense search hit (flattened
 * `remoteEligibilityScopeType`/`remoteEligibilityRef`, see to-typesense-job-record.ts) or the
 * `/api/getjob` record (nested `remoteEligibility`, as enrichJob.ts stores it).
 */
export interface StructuredLocationSource {
  /** Legacy freeform strings; only consulted to tell a remote-only job from one whose physical
   * (foreign) cities the gazetteer couldn't resolve yet, see resolveStructuredJobLocations. */
  location?: unknown;
  locationIds?: unknown;
  remoteEligibility?: { scopeType?: unknown; ref?: unknown } | null;
  remoteEligibilityScopeType?: unknown;
  remoteEligibilityRef?: unknown;
  /** TEC-482 follow-up: plural siblings of the two fields above, every remoteScope entry instead
   * of just the first. See resolveStructuredRemoteScopes. */
  remoteEligibilities?: Array<{ scopeType?: unknown; ref?: unknown }> | null;
  remoteEligibilityRefs?: unknown;
}

export interface ResolveStructuredJobLocationsOptions {
  locale: StructuredLocationLocale;
  /**
   * The viewer's active `regionTags` selection. A job entry that is one of these (or sits inside a
   * selected Finnish maakunta / inside Finland when Finland itself is selected) is shown first, the
   * same "show the city you searched for" rule the legacy card applies to the `location` filter.
   */
  selectedRegionTags?: readonly string[] | null;
  /**
   * Legacy card rule (see utils/data/filter-job-locations.ts): when a job has at least one
   * Finnish location, hide its non-Finland ones on the card, users see the rest on the job page.
   * Default true; the job page passes false and lists everything.
   */
  hideForeignWhenFinlandPresent?: boolean;
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.trim() !== '').map((v) => v.trim());
  }
  return typeof value === 'string' && value.trim() !== '' ? [value.trim()] : [];
}

/** "Remote - Europe", "Etätyö - Eurooppa", "Distans - Europa", or a bare "Remote"/"Etätyö"/"Distans":
 * a legacy pseudo-location that describes a remote scope, not a physical place. */
function isLegacyRemotePseudoLocation(value: string): boolean {
  const t = value.trim().toLowerCase().replace(/[\u2012-\u2015\u2212]/g, '-');
  return /^(remote|etätyö|etatyö|etä|distans|fjärr)(\s*[-:]\s*.+)?$/.test(t);
}

/**
 * True when at least one legacy location string names something more specific than the
 * structured entries resolved to (e.g. legacy "Mariehamn, Finland" vs. a structured result that
 * only resolved to country-level "Finland", because the gazetteer has no city-level placeId for
 * Mariehamn yet). Mirrors the existing "unresolved foreign city" fallback below, generalized to
 * domestic country-only resolutions: a legacy string's first comma segment that isn't just the
 * country name again means the LLM-extracted text is more granular than what the gazetteer gave us.
 */
function legacyLooksMoreSpecificThanEntries(legacyValues: string[], entries: StructuredJobLocationEntry[]): boolean {
  const entryLabels = new Set(entries.map((e) => e.label.trim().toLowerCase()));
  for (const value of legacyValues) {
    if (isLegacyRemotePseudoLocation(value)) continue;
    const firstSegment = value.split(',')[0]?.trim().toLowerCase();
    if (!firstSegment) continue;
    if (!entryLabels.has(firstSegment)) return true;
  }
  return false;
}

/**
 * The client label table only knows Finnish cities (plus countries/regions), so a foreign city's
 * placeId ("Saint-Ghislain", "Dublin") has no label and gets skipped, and a foreign city the
 * gazetteer only resolved to country level ("Eemshaven" -> The Netherlands) shows as the bare
 * country. Live example (2026-10-04): a Google posting for Eemshaven, Saint-Ghislain, Fredericia,
 * Hamina and Dublin rendered as "Hamina · The Netherlands", with three cities missing.
 *
 * Rebuild those foreign places from the legacy "City, Country" strings: label "City, Country"
 * (country in the viewer's locale), linked to the country's regionTags value. Bare country
 * entries a rebuilt city now covers are dropped. Finnish strings are left alone; the structured
 * Finnish entries already cover them.
 */
function mergeForeignLegacyCities(
  entries: StructuredJobLocationEntry[],
  legacyValues: string[],
  locale: StructuredLocationLocale,
): StructuredJobLocationEntry[] {
  const knownPlaceLabels = new Set(entries.filter((e) => e.type !== 'country').map((e) => e.label.trim().toLowerCase()));
  const extras: StructuredJobLocationEntry[] = [];
  const seenLabels = new Set<string>();
  for (const value of legacyValues) {
    if (isLegacyRemotePseudoLocation(value)) continue;
    const segments = value.split(',').map((s) => s.trim()).filter(Boolean);
    if (segments.length < 2) continue;
    const city = segments[0];
    if (knownPlaceLabels.has(city.toLowerCase())) continue;
    const countryPlaceId = findCountryRegionTagByName(segments[segments.length - 1]);
    if (!countryPlaceId || isFinlandRegionTagValue(countryPlaceId)) continue;
    const label = `${city}, ${getRegionTagLabel(countryPlaceId, locale)}`;
    if (seenLabels.has(label.toLowerCase())) continue;
    seenLabels.add(label.toLowerCase());
    extras.push({ placeId: countryPlaceId, label, type: 'city' });
  }
  if (extras.length === 0) return entries;
  const coveredCountries = new Set(extras.map((e) => e.placeId));
  return [...entries.filter((e) => !(e.type === 'country' && coveredCountries.has(e.placeId))), ...extras];
}

/** 3 = exactly what the viewer selected, 2 = inside a selected region, 1 = in Finland, 0 = elsewhere. */
function priorityScore(placeId: string, selected: readonly string[]): number {
  if (selected.includes(placeId)) return 3;
  const isFinland = isFinlandRegionTagValue(placeId);
  for (const sel of selected) {
    if (sel === FINLAND_COUNTRY_PLACE_ID && isFinland) return 2;
    if (getFinlandRegionChildren(sel).includes(placeId)) return 2;
  }
  return isFinland ? 1 : 0;
}

export function resolveStructuredJobLocations(
  job: StructuredLocationSource | null | undefined,
  options: ResolveStructuredJobLocationsOptions,
): StructuredJobLocationDisplay | null {
  if (!job) return null;
  const { locale, selectedRegionTags, hideForeignWhenFinlandPresent = true } = options;

  const seen = new Set<string>();
  let entries: StructuredJobLocationEntry[] = [];
  for (const placeId of toStringArray(job.locationIds)) {
    if (seen.has(placeId)) continue;
    seen.add(placeId);
    // An id the client-side label table doesn't know would render as a bare number; skip it and
    // let the caller fall back to legacy text if nothing labeled remains.
    if (!hasRegionTagLabel(placeId)) continue;
    entries.push({ placeId, label: getRegionTagLabel(placeId, locale), type: getRegionTagType(placeId) ?? 'city' });
  }
  if (entries.length === 0) {
    // No resolved physical place. Two different situations look the same here:
    //  - a genuinely remote-only job (legacy strings are all "Remote - X" pseudo-locations): show
    //    the structured scope as the location, linked to its regionTags value when it has one,
    //    so the card is fully on the new system instead of printing the legacy string;
    //  - a job in a foreign city the gazetteer doesn't seed yet ("Amsterdam, Netherlands", left
    //    unresolved by location-resolver.ts): the legacy city text is the more informative
    //    location, so return null and let the caller fall back to it (the remote scope still
    //    shows beside the work model).
    const legacy = toStringArray(job.location);
    if (legacy.some((v) => !isLegacyRemotePseudoLocation(v))) return null;
    const scope = resolveStructuredRemoteScope(job, locale);
    if (!scope) return null;
    const entry: StructuredJobLocationEntry = {
      placeId: scope.regionTagValue ?? '',
      label: scope.label,
      type: scope.regionTagValue ? getRegionTagType(scope.regionTagValue) ?? 'country' : 'global',
    };
    return { entries: [entry], primary: entry, additionalCount: 0, display: entry.label, fullList: entry.label, remoteScopeAsLocation: true };
  }

  if (entries.every((e) => e.type === 'country') && legacyLooksMoreSpecificThanEntries(toStringArray(job.location), entries)) {
    return null;
  }

  entries = mergeForeignLegacyCities(entries, toStringArray(job.location), locale);

  // "Hamina · The Netherlands" reads as one place. Once a Finnish city sits next to foreign
  // places, spell out its country too, so every entry has the same "City, Country" shape.
  const hasForeign = entries.some((e) => !isFinlandRegionTagValue(e.placeId));
  if (hasForeign && !(hideForeignWhenFinlandPresent && entries.some((e) => isFinlandRegionTagValue(e.placeId)))) {
    const finlandLabel = getRegionTagLabel(FINLAND_COUNTRY_PLACE_ID, locale);
    entries = entries.map((e) =>
      e.type === 'city' && isFinlandRegionTagValue(e.placeId) ? { ...e, label: `${e.label}, ${finlandLabel}` } : e,
    );
  }

  if (hideForeignWhenFinlandPresent && entries.some((e) => isFinlandRegionTagValue(e.placeId))) {
    entries = entries.filter((e) => isFinlandRegionTagValue(e.placeId));
  }

  const selected = (selectedRegionTags ?? []).filter((v): v is string => typeof v === 'string' && v !== '');
  entries = entries
    .map((entry, index) => ({ entry, index, score: priorityScore(entry.placeId, selected) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((x) => x.entry);

  const primary = entries[0];
  const additionalCount = entries.length - 1;
  return {
    entries,
    primary,
    additionalCount,
    display: additionalCount > 0 ? `${primary.label} +${additionalCount}` : primary.label,
    fullList: entries.map((e) => e.label).join(', '),
    remoteScopeAsLocation: false,
  };
}

/**
 * Job-page form: up to `max` labels joined with " · ", then " +N" for the rest,
 * e.g. "Helsinki · Espoo · Tampere +2". Same separator formatJobDetailLocationDisplay uses for
 * the legacy strings, so the flag-on and flag-off pages read alike.
 */
export function formatStructuredJobLocationLine(display: StructuredJobLocationDisplay, max = 3): string {
  const shown = display.entries.slice(0, Math.max(1, max));
  const rest = display.entries.length - shown.length;
  const line = shown.map((e) => e.label).join(' · ');
  return rest > 0 ? `${line} +${rest}` : line;
}

export type StructuredRemoteScopeType = 'global' | 'country' | 'continent' | 'namedRegion';

export interface StructuredRemoteScope {
  scopeType: StructuredRemoteScopeType;
  /** Per-locale label: "EMEA", "Eurooppa", "Globalt", "Ruotsi"... */
  label: string;
  /** The `regionTags` facet value that best matches this scope (a continent code, recruiting-region
   * code, or country placeId), for linking to "jobs in {scope}". Absent for a global scope. */
  regionTagValue?: string;
}

/** House terms for a worldwide remote scope, matching the legacy "Remote - Global" hub copy
 * (location-hub-config.ts / url-constants.ts: Global / Globaali / Globalt). Place-name vocabulary
 * like region-tag-labels.json, deliberately not a catalogue message. */
const GLOBAL_SCOPE_LABEL: Record<StructuredLocationLocale, string> = { en: 'Global', fi: 'Globaali', sv: 'Globalt' };

const SCOPE_TYPES: ReadonlySet<string> = new Set<StructuredRemoteScopeType>(['global', 'country', 'continent', 'namedRegion']);

/** Shared by resolveStructuredRemoteScope and resolveStructuredRemoteScopes: builds one scope from a raw scopeType/ref pair. */
function resolveScopeFromParts(rawType: unknown, rawRef: unknown, locale: StructuredLocationLocale): StructuredRemoteScope | null {
  if (typeof rawType !== 'string' || !SCOPE_TYPES.has(rawType)) return null;
  const scopeType = rawType as StructuredRemoteScopeType;

  if (scopeType === 'global') return { scopeType, label: GLOBAL_SCOPE_LABEL[locale] };

  const ref = typeof rawRef === 'string' ? rawRef.trim() : '';
  if (!ref) return null;

  if (scopeType === 'country') {
    const placeId = getRegionTagValueForCountryCode(ref);
    if (!placeId) return null;
    return { scopeType, label: getRegionTagLabel(placeId, locale), regionTagValue: placeId };
  }

  // continent / namedRegion refs are already regionTags values (continent codes, recruiting-region codes).
  const code = ref.toUpperCase();
  if (!hasRegionTagLabel(code)) return null;
  return { scopeType, label: getRegionTagLabel(code, locale), regionTagValue: code };
}

export function resolveStructuredRemoteScope(
  job: StructuredLocationSource | null | undefined,
  locale: StructuredLocationLocale,
): StructuredRemoteScope | null {
  if (!job) return null;
  const nested = job.remoteEligibility;
  const rawType = job.remoteEligibilityScopeType ?? nested?.scopeType;
  const rawRef = job.remoteEligibilityRef ?? nested?.ref;
  return resolveScopeFromParts(rawType, rawRef, locale);
}

/**
 * Plural sibling of resolveStructuredRemoteScope: every remoteScope entry, not just the first.
 * A job can list several discrete remote countries ("Remote - Germany", "Remote - Ireland",
 * "Remote - Spain", "Remote - United Kingdom" all on one posting). Same dual-source branching
 * (nested job-record shape vs. flattened Typesense hit shape) as the singular resolver above.
 * Empty array, never null, when nothing resolves.
 */
export function resolveStructuredRemoteScopes(
  job: StructuredLocationSource | null | undefined,
  locale: StructuredLocationLocale,
): StructuredRemoteScope[] {
  if (!job) return [];

  // Nested job-record shape (e.g. /api/getjob): remoteEligibilities is an array of {scopeType, ref}.
  if (Array.isArray(job.remoteEligibilities)) {
    return job.remoteEligibilities
      .map((entry) => resolveScopeFromParts(entry?.scopeType, entry?.ref, locale))
      .filter((scope): scope is StructuredRemoteScope => scope !== null);
  }

  // Flattened Typesense hit shape: remoteEligibilityRefs is one `scopeType:ref` composite per
  // entry (bare `global` has no ref), the encoding to-typesense-job-record.ts writes.
  const refs = toStringArray(job.remoteEligibilityRefs);
  if (refs.length > 0) {
    return refs
      .map((composite) => {
        const sep = composite.indexOf(':');
        const scopeType = sep === -1 ? composite : composite.slice(0, sep);
        const ref = sep === -1 ? undefined : composite.slice(sep + 1);
        return resolveScopeFromParts(scopeType, ref, locale);
      })
      .filter((scope): scope is StructuredRemoteScope => scope !== null);
  }

  // Neither plural field is populated (job not yet re-enriched since this shipped): fall back to
  // the singular fields so a not-yet-backfilled job still shows the one scope it has.
  const single = resolveStructuredRemoteScope(job, locale);
  return single ? [single] : [];
}

/**
 * "Etätyö (EMEA)" / "Remote (Global)": the work-model value with the structured scope in
 * parentheses; the scope alone when the job has no remoteness value; the plain remoteness when
 * there's no scope. Parentheses rather than " · " so the scope can't be mistaken for the next
 * separator-joined fact on the same line.
 */
export function formatRemotenessWithScope(remoteness: string | null | undefined, scope: StructuredRemoteScope | null): string {
  const base = (remoteness ?? '').trim();
  if (!scope) return base;
  return base ? `${base} (${scope.label})` : scope.label;
}

/**
 * Plural sibling of formatRemotenessWithScope: "Remote (Germany, Ireland, Spain, United Kingdom)"
 * for a job with several discrete remote countries. 0-1 scopes render identically to the singular
 * function (no behavior change for the common case); 2+ join their labels with a comma.
 */
export function formatRemotenessWithScopes(remoteness: string | null | undefined, scopes: StructuredRemoteScope[]): string {
  if (scopes.length <= 1) return formatRemotenessWithScope(remoteness, scopes[0] ?? null);
  const base = (remoteness ?? '').trim();
  const joined = scopes.map((s) => s.label).join(', ');
  return base ? `${base} (${joined})` : joined;
}
